import React from 'react';
import { useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DetailsCardDetailList, {
    SubjectProfileDetailsCardItem,
    formatDate,
    formatStringOrNumber,
} from './details-card-detail-list';
import styles from './index.module.scss';
import Card from '@components-clean/core/card';
import { InfoField } from '@components-clean/core/info-field';
import Loader from '@components-clean/core/loader';
import { useTranslation } from '@hooks/index';
import { GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION } from '@queries/subject-monitoring';
import { SubjectMonitoringAPI } from '@apis/bff-api/subject-monitoring/subject-profile';

type Props = {
    studyRef: string;
    datasetId: number;
    subjectId: number;
};

const SubjectProfileDetailsTreatmentStatusCard = ({ studyRef, datasetId, subjectId }: Props) => {
    const { t: tCommon } = useTranslation('common');

    const { t } = useTranslation('sm', {
        keyPrefix: 'subjectProfile.treatmentStatus',
    });

    const getSubjectDetailsQuery = SubjectMonitoringAPI.useGetSubjectDetails(studyRef, datasetId, subjectId);
    const getSubjectProfileDetailsConfigQuery = useQuery(GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION, {
        variables: { studyId: studyRef },
    });

    const subjectDetails = getSubjectDetailsQuery.data;
    const sectionConfig =
        getSubjectProfileDetailsConfigQuery.data?.subjectProfileDetailsConfiguration
            ?.subjectProfileDetailsConfigurationItem?.treatmentStatus;

    const isLoading = getSubjectDetailsQuery.isLoading || getSubjectProfileDetailsConfigQuery.loading;

    const isError = !!getSubjectDetailsQuery.error || !!getSubjectProfileDetailsConfigQuery.error;

    const items: SubjectProfileDetailsCardItem[] = [];

    if (sectionConfig?.cohort) {
        items.push({
            key: 'cohort',
            label: t('cohort'),
            value: formatStringOrNumber(subjectDetails?.cohort),
        });
    }
    if (sectionConfig?.treatmentArm) {
        items.push({
            key: 'treatmentArm',
            label: t('treatmentArm'),
            value: formatStringOrNumber(subjectDetails?.treatmentArm),
        });
    }
    if (sectionConfig?.txStart) {
        items.push({
            key: 'txStart',
            label: t('txStart'),
            value: formatDate(subjectDetails?.txStartDate),
        });
    }
    if (sectionConfig?.txDisc) {
        items.push({
            key: 'txDisc',
            label: t('txDiscDate'),
            value: formatDate(subjectDetails?.txDiscontinuedDate),
        });
    }
    if (sectionConfig?.txDiscReason) {
        items.push({
            key: 'txDiscReason',
            label: t('txDiscReason'),
            value: formatStringOrNumber(subjectDetails?.txDiscontinuedReason),
        });
    }
    if (sectionConfig?.txCompleted) {
        items.push({
            key: 'txCompleted',
            label: t('completedTxDate'),
            value: formatDate(subjectDetails?.txCompletedDate),
        });
    }

    if (!isLoading && !isError && items.length === 0) {
        return null;
    }

    return (
        <Card
            data-testid="subject-profile-details-treatment-status-card"
            title={sectionConfig?.label ?? 'Treatment'}
            padding="none"
            collapsible
        >
            <div className={styles.cardIconAndDetailsListWrapper}>
                <div className={styles.cardIconWrapper}>
                    <FontAwesomeIcon icon={['fas', 'syringe']} className={styles.cardIcon} />
                </div>

                {isLoading || isError ? (
                    <div className={styles.cardLoadingStateWrapper}>
                        {isError ? (
                            <InfoField type="Error" message={tCommon('errorMessage')} />
                        ) : isLoading ? (
                            <Loader />
                        ) : null}
                    </div>
                ) : (
                    <DetailsCardDetailList items={items} />
                )}
            </div>
        </Card>
    );
};

export default SubjectProfileDetailsTreatmentStatusCard;
